// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Import Tools
// Bulk CSV upload, import from Impact module, map-pick zones
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, SimpleGrid,
  Table, Textarea, Select, FileButton,
} from '../../components/gab/GabComponents';
import {
  IconUpload, IconFileSpreadsheet, IconMapPin, IconBuildingCommunity,
  IconCheck, IconX,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import type { DebrisZone, DebrisTruck, DebrisContractor } from '../../types/debris';
import { primary } from '../../lib/colors';

type ImportTarget = 'zones' | 'trucks' | 'contractors' | 'crews';

const IMPORT_TARGETS: { value: ImportTarget; label: string; columns: string }[] = [
  { value: 'zones', label: 'Zones', columns: 'zoneName, zoneCode, status, centerLat, centerLng, estimatedCubicYards, debrisTypes, notes' },
  { value: 'trucks', label: 'Trucks', columns: 'truckNumber, licensePlate, truckType, contractorId, lengthFt, widthFt, heightFt, cubicYardCapacity, status' },
  { value: 'contractors', label: 'Contractors', columns: 'companyName, contractNumber, contactName, contactPhone, contactEmail, rateVegetative, rateCAndD, status' },
  { value: 'crews', label: 'Crews / Drivers', columns: 'crewName, crewType, contractorId, driverName, driverPhone, driverEmail, assignedZoneId, status' },
];

export default function Import() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const { createZone, createTruck, createContractor, createCrew } = useDebrisStore();

  const [target, setTarget] = useState<ImportTarget>('zones');
  const [csvData, setCsvData] = useState('');
  const [parsedRows, setParsedRows] = useState<Record<string, string>[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<{ success: number; failed: number } | null>(null);

  // Parse a single CSV line respecting quoted fields
  const splitCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') { current += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { current += ch; }
      } else {
        if (ch === '"') { inQuotes = true; }
        else if (ch === ',') { result.push(current.trim()); current = ''; }
        else { current += ch; }
      }
    }
    result.push(current.trim());
    return result;
  };

  // Parse CSV text
  const parseCSV = (text: string) => {
    const lines = text.trim().split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) {
      notifications.show({ message: 'CSV must have a header row and at least one data row', color: 'yellow' });
      return;
    }
    const hdrs = splitCSVLine(lines[0]);
    setHeaders(hdrs);
    const rows = lines.slice(1).map(line => {
      const vals = splitCSVLine(line);
      const row: Record<string, string> = {};
      hdrs.forEach((h, i) => { row[h] = vals[i] ?? ''; });
      return row;
    });
    setParsedRows(rows);
    setImportResults(null);
  };

  const handleFileUpload = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      setCsvData(text);
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  const handlePasteImport = () => {
    if (!csvData.trim()) {
      notifications.show({ message: 'Paste CSV data first', color: 'yellow' });
      return;
    }
    parseCSV(csvData);
  };

  const runImport = async () => {
    if (parsedRows.length === 0) return;
    setImporting(true);
    let success = 0;
    let failed = 0;

    for (const row of parsedRows) {
      try {
        if (target === 'zones') {
          await createZone({ ...row, incidentId } as Partial<DebrisZone>);
        } else if (target === 'trucks') {
          await createTruck({ ...row, incidentId } as Partial<DebrisTruck>);
        } else if (target === 'contractors') {
          await createContractor({ ...row, incidentId } as Partial<DebrisContractor>);
        } else if (target === 'crews') {
          await createCrew({ ...row, incidentId } as unknown as Parameters<typeof createCrew>[0]);
        }
        success++;
      } catch {
        failed++;
      }
    }

    setImportResults({ success, failed });
    setImporting(false);
    notifications.show({
      message: `Import complete: ${success} succeeded, ${failed} failed`,
      color: failed > 0 ? 'yellow' : 'green',
    });
  };

  const downloadTemplate = () => {
    const tgt = IMPORT_TARGETS.find(t => t.value === target);
    if (!tgt) return;
    const csv = tgt.columns + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debris-${target}-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const targetInfo = IMPORT_TARGETS.find(t => t.value === target);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Import Data</Title>
      </Group>

      {/* Import method cards */}
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Paper p="md" radius="md" withBorder>
          <Group gap="xs" mb="xs">
            <IconFileSpreadsheet size={20} color={primary[700]} />
            <Text fw={700}>CSV Upload</Text>
          </Group>
          <Text size="xs" c="dimmed" mb="sm">
            Upload a CSV file with zone, truck, contractor, or crew data
          </Text>
          <FileButton onChange={handleFileUpload} accept=".csv,text/csv">
            {(props) => <Button {...props} size="xs" variant="light" leftSection={<IconUpload size={14} />}>Choose File</Button>}
          </FileButton>
        </Paper>

        <Paper p="md" radius="md" withBorder>
          <Group gap="xs" mb="xs">
            <IconMapPin size={20} color="#16a34a" />
            <Text fw={700}>Paste CSV</Text>
          </Group>
          <Text size="xs" c="dimmed" mb="sm">
            Paste CSV data directly from a spreadsheet
          </Text>
          <Button size="xs" variant="light" onClick={handlePasteImport}
            leftSection={<IconCheck size={14} />}>Parse Pasted Data</Button>
        </Paper>

        <Paper p="md" radius="md" withBorder>
          <Group gap="xs" mb="xs">
            <IconBuildingCommunity size={20} color="#d97706" />
            <Text fw={700}>Impact Integration</Text>
          </Group>
          <Text size="xs" c="dimmed" mb="sm">
            Import damage assessment locations as debris zones
          </Text>
          <Badge color="gray" variant="light">Coming Soon</Badge>
        </Paper>
      </SimpleGrid>

      {/* Target selector and template */}
      <Paper p="md" radius="md" withBorder>
        <Group gap="md" mb="md">
          <Select label="Import Target" data={IMPORT_TARGETS.map(t => ({ value: t.value, label: t.label }))}
            value={target} onChange={v => { setTarget(v as ImportTarget); setParsedRows([]); setHeaders([]); setImportResults(null); }}
            size="xs" w={180} />
          <div style={{ flex: 1 }}>
            <Text size="xs" c="dimmed">Expected columns:</Text>
            <Text size="xs" ff="monospace">{targetInfo?.columns}</Text>
          </div>
          <Button size="xs" variant="subtle" onClick={downloadTemplate}>Download Template</Button>
        </Group>

        {/* Paste area */}
        <Textarea
          label="CSV Data"
          placeholder={`Paste CSV data here...\n${targetInfo?.columns}\nvalue1, value2, ...`}
          value={csvData}
          onChange={e => setCsvData(e.target.value)}
          autosize
          minRows={4}
          maxRows={10}
          mb="md"
        />

        {/* Preview parsed data */}
        {parsedRows.length > 0 && (
          <>
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={600}>
                Preview ({parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''})
              </Text>
              <Button size="xs" onClick={runImport} loading={importing}
                leftSection={<IconUpload size={14} />}>
                Import {parsedRows.length} Records
              </Button>
            </Group>

            <div style={{ overflowX: 'auto', maxHeight: 400 }}>
              <Table striped withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40}>#</Table.Th>
                    {headers.map(h => <Table.Th key={h}>{h}</Table.Th>)}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {parsedRows.slice(0, 50).map((row, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>{i + 1}</Table.Td>
                      {headers.map(h => <Table.Td key={h}><Text size="xs">{row[h] || '-'}</Text></Table.Td>)}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
            {parsedRows.length > 50 && (
              <Text size="xs" c="dimmed" mt="xs">Showing first 50 of {parsedRows.length} rows</Text>
            )}
          </>
        )}

        {/* Import results */}
        {importResults && (
          <Paper p="sm" mt="md" radius="sm" withBorder
            style={{ borderColor: importResults.failed > 0 ? 'var(--mantine-color-yellow-5)' : 'var(--mantine-color-green-5)' }}>
            <Group gap="xs">
              {importResults.failed === 0 ? <IconCheck size={16} color="green" /> : <IconX size={16} color="orange" />}
              <Text size="sm">
                <strong>{importResults.success}</strong> records imported successfully
                {importResults.failed > 0 && <>, <strong>{importResults.failed}</strong> failed</>}
              </Text>
            </Group>
          </Paper>
        )}
      </Paper>
    </Stack>
  );
}
